import { useMemo } from 'react';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads.ts';
import {
	LocationValue,
	getOpenCommentThreads,
	getAnnotationThreadKeys,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { fg } from '@confluence/feature-gating';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useCommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { SitePermissionType } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';
import type {
	CommentType,
	ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal } from '@confluence/feature-experiments';

export const useFooterComments = (): {
	footerCommentIds: string[];
	footerCommentThreads: (
		| CommentData
		| ({
				readonly author: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly permissionType: SitePermissionType | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
					readonly type: string | null;
				};
				readonly body: {
					readonly value: string;
				};
				readonly container: {
					readonly id: string | null;
				};
				readonly contentStatus: string;
				readonly contributors: ReadonlyArray<{
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				} | null> | null;
				readonly createdAt: {
					readonly value: string;
				};
				readonly createdAtNonLocalized: string;
				readonly id: string;
				readonly links: {
					readonly editui: string | null;
					readonly webui: string | null;
				};
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				readonly location: {
					readonly __typename: string;
					readonly commentResolveProperties?: {
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineMarkerRef?: string | null;
					readonly inlineResolveProperties?: {
						readonly isDangling: boolean;
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineText?: string | null;
					readonly type: string;
				};
				readonly parentId: string | null;
				readonly permissions: {
					readonly isEditable: boolean;
					readonly isRemovable: boolean;
					readonly isResolvable: boolean;
				};
				readonly reactionsSummary: {
					readonly ari: string;
					readonly containerAri: string;
					readonly reactionsCount: number;
					readonly reactionsSummaryForEmoji: ReadonlyArray<{
						readonly count: number;
						readonly emojiId: string;
						readonly id: string;
						readonly reacted: boolean;
					} | null>;
				} | null;
				readonly replies: ReadonlyArray<{
					readonly author: {
						readonly __typename: string;
						readonly accountId?: string | null;
						readonly displayName: string | null;
						readonly permissionType: SitePermissionType | null;
						readonly profilePicture: {
							readonly path: string;
						} | null;
					};
					readonly body: {
						readonly value: string;
					};
					readonly contentStatus: string;
					readonly contributors: ReadonlyArray<{
						readonly __typename: string;
						readonly accountId?: string | null;
						readonly displayName: string | null;
						readonly profilePicture: {
							readonly path: string;
						} | null;
					} | null> | null;
					readonly createdAt: {
						readonly value: string;
					};
					readonly createdAtNonLocalized: string;
					readonly id: string;
					readonly links: {
						readonly editui: string | null;
						readonly webui: string | null;
					};
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					readonly location: {
						readonly __typename: string;
						readonly commentResolveProperties?: {
							readonly resolved: boolean;
							readonly resolvedFriendlyDate: string | null;
							readonly resolvedTime: AGG$Long | null;
							readonly resolvedUser: string | null;
						} | null;
						readonly inlineMarkerRef?: string | null;
						readonly inlineResolveProperties?: {
							readonly isDangling: boolean;
							readonly resolved: boolean;
							readonly resolvedFriendlyDate: string | null;
							readonly resolvedTime: AGG$Long | null;
							readonly resolvedUser: string | null;
						} | null;
						readonly inlineText?: string | null;
						readonly type: string;
					};
					readonly parentId: string | null;
					readonly permissions: {
						readonly isEditable: boolean;
						readonly isRemovable: boolean;
						readonly isResolvable: boolean;
					};
					readonly reactionsSummary: {
						readonly ari: string;
						readonly containerAri: string;
						readonly reactionsCount: number;
						readonly reactionsSummaryForEmoji: ReadonlyArray<{
							readonly count: number;
							readonly emojiId: string;
							readonly id: string;
							readonly reacted: boolean;
						} | null>;
					} | null;
					readonly version: {
						readonly by: {
							readonly __typename: string;
							readonly displayName: string | null;
						} | null;
						readonly number: number | null;
						readonly when: string | null;
					};
				} | null>;
				readonly version: {
					readonly by: {
						readonly __typename: string;
						readonly accountId?: string | null;
						readonly displayName: string | null;
						readonly type: string | null;
					} | null;
					readonly friendlyWhen: string | null;
					readonly number: number | null;
					readonly when: string | null;
				};
		  } & {
				type: CommentType;
				isOpen: boolean;
				wasRemovedByAnotherUser: boolean;
				replies: ReplyData[];
		  })
	)[];
} => {
	const {
		orderedActiveAnnotationIdList,
		commentsDataMap: legacyCommentsDataMap,
		removedThreadsMap,
	} = useCommentsDataState();

	const pageMode = useGetPageMode();

	// Get processed comments data for new comments data manager
	const processedCommentsData = useProcessedCommentsDataState();

	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? processedCommentsData.commentsDataMap
		: legacyCommentsDataMap;

	return useMemo(() => {
		const footerCommentThreads = fg('confluence_frontend_content_wrapper')
			? getVisibleCommentThreads({
					includeGeneralComments: true,
					commentsDataMap,
					includeInlineComments: false,
					orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
					pageMode,
				})
			: getOpenCommentThreads({
					commentsDataMap,
					showInlineComments: false,
					showGeneralComments: true,
					location: LocationValue.FOOTER,
					orderedActiveAnnotationIdList,
					removedThreadsMap,
				});

		const footerCommentIds = footerCommentThreads.map((thread) => thread.id);

		return {
			footerCommentIds,
			footerCommentThreads,
		};
	}, [commentsDataMap, orderedActiveAnnotationIdList, pageMode, removedThreadsMap]);
};
